import React, { useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { ArrowLeft, User, Filter, ChevronUp, ChevronDown } from 'lucide-react';
import { trovaSquadraGiocatore, calcolaPuntiRanking } from './util';
import { LinkGiocatore, LinkSquadra } from '../../../components/statistiche/Link';
import { urlEdizione } from '../links';
import '../Statistiche.css';

// Le colonne che l'URL può chiedere di ordinare. L'elenco è chiuso perché ?ordina= arriva da
// fuori: un valore inventato deve ricadere sull'ordinamento di default, non produrre una tabella
// ordinata per una chiave che non esiste.
const ORDINAMENTI = ['nome', 'squadra', 'numero', 'mete', 'pieni', 'vuoti', 'assist', 'ranking'];

const ElencoGiocatori = ({ statistiche, idEdizione, ordinaIniziale }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSquadra, setFilterSquadra] = useState('');
  const [sortBy, setSortBy] = useState(ordinaIniziale || 'nome');
  // Un ordinamento chiesto dall'URL è sempre "dal migliore": chi clicca la tile delle mete vuole
  // vedere in cima chi ne ha fatte di più, non la lista alfabetica.
  const [sortDirection, setSortDirection] = useState(ordinaIniziale ? 'desc' : 'asc');

  // L'id è la chiave giusta: il nome sul giocatore non distingue due squadre omonime della
  // stessa edizione, e cercarlo per nome le riunirebbe di nuovo silenziosamente, come slugDaId
  // in edizione/squadre.js chiude per le altre viste. trovaSquadraGiocatore resta un ripiego per
  // chi ha in mano solo la maglia, non un id.
  const squadraDiGiocatore = (giocatore) =>
    statistiche.squadre.find((s) => s.idSquadra === giocatore.idSquadra)
    || trovaSquadraGiocatore(giocatore, statistiche.squadre);

  const getSquadraGiocatore = (giocatore) => {
    const squadra = squadraDiGiocatore(giocatore);
    return squadra ? squadra.nome : (giocatore.maglia || 'Squadra N/A');
  };

  const getGruppoGiocatore = (giocatore) => squadraDiGiocatore(giocatore)?.gruppo || '';

  // Il nome resta quello che si legge oggi: linkato solo se dietro c'è una squadra vera e quella
  // squadra ha una storica, perché una maglia non ha una scheda e neanche una squadra senza
  // identità nel tempo.
  const SquadraDelGiocatore = ({ giocatore }) => (
    <LinkSquadra slug={squadraDiGiocatore(giocatore)?.slugStorica} nome={squadraDiGiocatore(giocatore)?.nome}>
      {getSquadraGiocatore(giocatore)}
    </LinkSquadra>
  );

  const sesso = (g) => (g.sesso || '').toLowerCase();

  // Ottieni lista squadre per il filtro
  const squadre = [...new Set(statistiche.giocatori.map(g => getSquadraGiocatore(g)))].sort();

  // Gestisce l'ordinamento delle colonne
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection(field === 'nome' || field === 'squadra' ? 'asc' : 'desc');
    }
  };

  // Filtra e ordina i giocatori
  const giocatoriFiltrati = statistiche.giocatori
    .filter(giocatore => {
      const nomeMatch = giocatore.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (giocatore.numero != null && giocatore.numero.toString().includes(searchTerm));

      const squadraGiocatore = getSquadraGiocatore(giocatore);
      const matchSquadra = !filterSquadra || squadraGiocatore === filterSquadra;

      return nomeMatch && matchSquadra;
    })
    .sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'nome':
          return dir * a.nome.localeCompare(b.nome);
        case 'squadra':
          return dir * getSquadraGiocatore(a).localeCompare(getSquadraGiocatore(b));
        case 'numero':
          return dir * ((a.numero || 0) - (b.numero || 0));
        case 'mete':
          return dir * ((a.mete || 0) - (b.mete || 0));
        case 'pieni':
          return dir * ((a.pieni || 0) - (b.pieni || 0));
        case 'vuoti':
          return dir * ((a.vuoti || 0) - (b.vuoti || 0));
        case 'assist':
          return dir * ((a.assist || 0) - (b.assist || 0));
        case 'ranking':
          return dir * (calcolaPuntiRanking(a) - calcolaPuntiRanking(b));
        default:
          return 0;
      }
    });

  // Header ordinabile — stesso markup/stile di SquadreView
  const SortableHeader = ({ field, children }) => (
    <th className="squadre-table-header sortable" onClick={() => handleSort(field)}>
      <div className="header-content">
        <span>{children}</span>
        <div className="sort-icons">
          <ChevronUp className={`sort-icon ${sortBy === field && sortDirection === 'asc' ? 'active' : ''}`} />
          <ChevronDown className={`sort-icon ${sortBy === field && sortDirection === 'desc' ? 'active' : ''}`} />
        </div>
      </div>
    </th>
  );

  const podio = (i) => (i === 0 ? 'first' : i === 1 ? 'second' : i === 2 ? 'third' : 'other');

  // Tabella desktop (stile identico all'elenco squadre)
  const GiocatoriTable = () => (
    <div className="squadre-table-container">
      <table className="squadre-table">
        <thead>
          <tr>
            <th className="squadre-table-header rank-header">#</th>
            <SortableHeader field="squadra">Squadra</SortableHeader>
            <th className="squadre-table-header">Gruppo</th>
            <SortableHeader field="nome">Giocatore</SortableHeader>
            <th className="squadre-table-header">Sesso</th>
            <SortableHeader field="numero">N.</SortableHeader>
            <th className="squadre-table-header">Maglia</th>
            <SortableHeader field="mete">Mete</SortableHeader>
            <SortableHeader field="pieni">Pieni</SortableHeader>
            <SortableHeader field="vuoti">Vuoti</SortableHeader>
            <SortableHeader field="assist">Assist</SortableHeader>
            <SortableHeader field="ranking">Punti</SortableHeader>
          </tr>
        </thead>
        <tbody>
          {giocatoriFiltrati.map((giocatore, index) => (
            <tr key={`${giocatore.nome}-${giocatore.numero}-${index}`} className="squadre-table-row">
              <td className="rank-cell">
                <div className={`rank-badge-small ${podio(index)}`}>{index + 1}</div>
              </td>
              <td className="squadra-name-cell"><SquadraDelGiocatore giocatore={giocatore} /></td>
              <td className="squadra-name-cell">{getGruppoGiocatore(giocatore)}</td>
              <td className="squadra-name-cell">
                <span className="squadra-name">
                  <LinkGiocatore id={giocatore.idGiocatore} nome={giocatore.nome} cognome={giocatore.cognome} />
                </span>
              </td>
              <td className="stat-cell">
                <span className={`gender-badge ${sesso(giocatore)}`}>
                  {sesso(giocatore) === 'm' ? '♂' : '♀'}
                </span>
              </td>
              <td className="stat-cell"><span className="numero-badge">#{giocatore.numero}</span></td>
              <td className="squadra-name-cell">{giocatore.maglia}</td>
              <td className="stat-cell">{giocatore.mete || 0}</td>
              <td className="stat-cell">{giocatore.pieni || 0}</td>
              <td className="stat-cell">{giocatore.vuoti || 0}</td>
              <td className="stat-cell">{giocatore.assist || 0}</td>
              <td className="stat-cell points-cell">
                <span className="points-value">{calcolaPuntiRanking(giocatore)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Card mobile (stile identico all'elenco squadre)
  const GiocatoreCardCompact = ({ giocatore, index }) => {
    const gruppo = getGruppoGiocatore(giocatore);
    return (
      <div className="squadre-card-compact">
        <div className="squadre-card-compact-header">
          <div className={`squadre-rank-badge-compact ${podio(index)}`}>{index + 1}</div>
          <div className="squadre-card-compact-title">
            <h3>
              <LinkGiocatore id={giocatore.idGiocatore} nome={giocatore.nome} cognome={giocatore.cognome} />
            </h3>
            <p>
              <span className={`gender-badge ${sesso(giocatore)}`}>{sesso(giocatore) === 'm' ? '♂' : '♀'}</span>
              {' '}#{giocatore.numero}{giocatore.maglia ? ` · ${giocatore.maglia}` : ''}
            </p>
            <p><SquadraDelGiocatore giocatore={giocatore} />{gruppo ? ` · ${gruppo}` : ''}</p>
          </div>
        </div>

        <div className="squadre-stats-compact">
          <div className="stat-compact">
            <span className="stat-compact-value">{giocatore.mete || 0}</span>
            <span className="stat-compact-label">Mete</span>
          </div>
          <div className="stat-compact">
            <span className="stat-compact-value">{giocatore.pieni || 0}</span>
            <span className="stat-compact-label">Pieni</span>
          </div>
          <div className="stat-compact">
            <span className="stat-compact-value">{giocatore.vuoti || 0}</span>
            <span className="stat-compact-label">Vuoti</span>
          </div>
          <div className="stat-compact">
            <span className="stat-compact-value">{giocatore.assist || 0}</span>
            <span className="stat-compact-label">Assist</span>
          </div>
          <div className="stat-compact points">
            <span className="stat-compact-value">{calcolaPuntiRanking(giocatore)}</span>
            <span className="stat-compact-label">Punti</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="detail-view">
      <div className="detail-header">
        <Link to={urlEdizione(idEdizione)} className="back-button">
          <ArrowLeft size={20} />
          Torna all'edizione
        </Link>
        <div className="detail-title">
          <User className="detail-icon" />
          <h2>Tutti i Giocatori ({giocatoriFiltrati.length})</h2>
        </div>
      </div>

      {/* Filtri e ricerca */}
      <div className="squadre-search-container">
        <div className="filters-row">
          <select
            value={filterSquadra}
            onChange={(e) => setFilterSquadra(e.target.value)}
            className="filter-select"
          >
            <option value="">Tutte le squadre</option>
            {squadre.map(squadra => (
              <option key={squadra} value={squadra}>{squadra}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Cerca per nome o numero..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {giocatoriFiltrati.length === 0 ? (
        <div className="no-results">
          <Filter className="no-results-icon" />
          <h3>Nessun giocatore trovato</h3>
          <p>Prova a modificare i filtri di ricerca</p>
        </div>
      ) : (
        <>
          <div className="squadre-desktop-view">
            <GiocatoriTable />
          </div>
          <div className="squadre-mobile-view">
            <div className="squadre-grid-compact">
              {giocatoriFiltrati.map((giocatore, index) => (
                <GiocatoreCardCompact
                  key={`${giocatore.nome}-${giocatore.numero}-${index}`}
                  giocatore={giocatore}
                  index={index}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * L'elenco dei giocatori di una edizione, rotta figlia di `EdizioneLayout`: le statistiche
 * arrivano dall'Outlet. L'ordinamento chiesto dalle tile del riepilogo viaggia in `?ordina=`,
 * così «i marcatori di questa edizione» è un indirizzo che si può mandare a qualcuno.
 *
 * Questa vista resta divisa in due componenti anche dopo che il caricamento è salito nel layout:
 * l'involucro serve ancora per la `key` qui sotto, che non potrebbe stare sul componente stesso.
 */
const GiocatoriView = () => {
  const { statistiche, idEdizione } = useOutletContext();
  const [searchParams] = useSearchParams();

  const ordina = searchParams.get('ordina');
  const ordinaIniziale = ORDINAMENTI.includes(ordina) ? ordina : null;

  return (
    <ElencoGiocatori
      // Cambiare ?ordina= deve ripartire dall'ordinamento chiesto: senza key React riuserebbe
      // lo stato del montaggio precedente e la tabella resterebbe ordinata come prima.
      key={ordinaIniziale || 'nome'}
      statistiche={statistiche}
      idEdizione={idEdizione}
      ordinaIniziale={ordinaIniziale}
    />
  );
};

export default GiocatoriView;
